import { Box } from '@material-ui/core'
import { ConfirmationEventTitleHelper } from '@shared/core/confirmation'
import { InboundNotificationListItem } from '@shared/core/inbound-sms'
import classNames from 'classnames'
import { formatInTimeZone } from 'date-fns-tz'
import { MouseEvent } from 'react'
import { Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import classes from './inbound-notifications-panel.module.scss'

type Props = {
  notifications: InboundNotificationListItem[]
  onToggleRead: (id: string, isCurrentlyRead: boolean) => void
}

function getIntentPillClass(intent: string): string {
  if (intent === 'confirm') {
    return classes.intentPillConfirm
  }
  if (intent === 'cancel') {
    return classes.intentPillCancel
  }
  return classes.intentPillDefault
}

function getIntentPillLabel(intent: string): string {
  if (intent === 'confirm') {
    return 'Confirmed'
  }
  if (intent === 'cancel') {
    return 'Cancelled'
  }
  if (!intent) {
    return 'Message'
  }
  return intent.charAt(0).toUpperCase() + intent.slice(1).replace(/_/g, ' ')
}

export function InboundNotificationsPanel({ notifications, onToggleRead }: Props) {
  const { timezone } = useDisplayTimezone()

  if (notifications.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="span" className={classes.emptyStateText}>
          You&apos;ll see notifications here when your clients reply to your reminders
        </Typography>
      </Box>
    )
  }

  const handleToggleRead = (event: MouseEvent<HTMLButtonElement>, id: string, isCurrentlyRead: boolean) => {
    event.stopPropagation()
    onToggleRead(id, isCurrentlyRead)
  }

  return (
    <Box className={classes.panel}>
      {notifications.map((notification) => {
        const isDeleted = Boolean(notification.event_deleted_at_snapshot)
        const isRead = notification.read_at != null

        return (
          <Box
            key={notification.id}
            className={classNames(classes.card, {
              [classes.cardUnread]: !isRead,
              [classes.cardDeleted]: isDeleted,
            })}
          >
            <Box className={classes.cardHeader}>
              <Typography variant="span" className={classes.appointmentCode}>
                {ConfirmationEventTitleHelper.removeStatus(notification.event_title_snapshot)}
              </Typography>
            </Box>
            {notification.intent !== 'free_form' ? (
              <span className={getIntentPillClass(notification.intent)}>{getIntentPillLabel(notification.intent)}</span>
            ) : null}
            {isDeleted ? <span className={classes.deletedPill}>Event deleted</span> : null}
            <Typography variant="span" className={classes.message}>
              {notification.message_preview}
            </Typography>
            <Box className={classes.cardFooter}>
              <Typography variant="span" className={classes.timestamp}>
                {formatInTimeZone(new Date(notification.created_at), timezone, 'MMM d, h:mm a')}
              </Typography>
              <button
                type="button"
                className={classes.readToggle}
                onClick={(event) => handleToggleRead(event, notification.id, isRead)}
              >
                {isRead ? 'Mark as unread' : 'Mark as read'}
              </button>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
